import React, { useCallback, useEffect, useState } from 'react';
import {
  Camera, Upload, Trash2, Loader2, AlertTriangle,
  ChevronDown, ChevronRight, X as XIcon, ImageOff,
} from 'lucide-react';
import toast from 'react-hot-toast';

const MAX_BYTES = 10 * 1024 * 1024;
const MAX_PHOTOS = 30;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

const fmtSize = (n) => {
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

function authedFetch(url, opts = {}) {
  const token = localStorage.getItem('assetlens_token');
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

/**
 * Thumbnail grid + lightbox. Each thumbnail fetches a fresh signed URL on
 * mount; the URL has a 15-minute TTL which is plenty for a single page view.
 * For HEIC, browsers that can't render fall back to an "Image" placeholder.
 */
function Thumb({ photo, onClick, onDelete, readonly }) {
  const [url, setUrl] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authedFetch(`/api/photos/${photo.id}/url`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { if (!cancelled) setUrl(d.url); })
      .catch(() => { if (!cancelled) setErrored(true); });
    return () => { cancelled = true; };
  }, [photo.id]);

  const isHeic = /heic|heif/i.test(photo.content_type || '');

  return (
    <div className="relative group rounded-lg overflow-hidden bg-slate-800 border border-slate-700 aspect-square">
      <button
        onClick={onClick}
        className="block w-full h-full focus:outline-none focus:ring-2 focus:ring-blue-500"
        title={photo.caption || photo.filename}
      >
        {url && !errored ? (
          <img
            src={url}
            alt={photo.caption || photo.filename}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={() => setErrored(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 text-xs gap-1">
            {errored ? <ImageOff size={20} /> : <Loader2 size={20} className="animate-spin" />}
            {isHeic && errored && <span>HEIC</span>}
          </div>
        )}
      </button>
      {!readonly && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(photo.id); }}
          className="absolute top-1 right-1 bg-rose-500/80 hover:bg-rose-500 text-white rounded-md p-1 opacity-0 group-hover:opacity-100 transition"
          title="Delete photo"
        >
          <Trash2 size={12} />
        </button>
      )}
      {photo.caption && (
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 text-white text-[10px] truncate">
          {photo.caption}
        </div>
      )}
    </div>
  );
}

function Lightbox({ photo, onClose }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!photo) return;
    authedFetch(`/api/photos/${photo.id}/url`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setUrl(d.url))
      .catch(() => toast.error('Failed to load photo'));
  }, [photo]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!photo) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white"
        aria-label="Close"
      >
        <XIcon size={28} />
      </button>
      {url ? (
        <img
          src={url}
          alt={photo.caption || photo.filename}
          className="max-w-full max-h-[90vh] object-contain"
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <Loader2 size={32} className="text-white animate-spin" />
      )}
      {photo.caption && (
        <div className="absolute bottom-6 inset-x-0 text-center text-white text-sm">{photo.caption}</div>
      )}
    </div>
  );
}

/**
 * Per-property photo gallery. Owner uploads + deletes; brokers viewing a
 * shared portfolio can see (read-only) but not modify. Pass `readonly`
 * from the parent when rendering for a broker.
 */
export default function PhotosPanel({ userPropertyId, readonly = false }) {
  const [photos, setPhotos]   = useState(null);
  const [error, setError]     = useState(null);
  const [open, setOpen]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const refresh = useCallback(() => {
    authedFetch(`/api/photos/by-property/${userPropertyId}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => { setPhotos(d); setError(null); })
      .catch(e => setError(e.message));
  }, [userPropertyId]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    if (photos && photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos per property — only ${MAX_PHOTOS - photos.length} slot${MAX_PHOTOS - photos.length === 1 ? '' : 's'} left`);
      return;
    }

    setUploading(true);
    let success = 0;
    for (const file of files) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: too large (max ${MAX_BYTES / 1024 / 1024} MB)`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('user_property_id', String(userPropertyId));
      try {
        const r = await authedFetch('/api/photos/upload', { method: 'POST', body: fd });
        if (!r.ok) {
          const detail = await r.json().catch(() => ({}));
          toast.error(`${file.name}: ${detail.detail || 'upload failed'}`);
        } else {
          success += 1;
        }
      } catch {
        toast.error(`${file.name}: upload failed`);
      }
    }
    if (success > 0) toast.success(`${success} photo${success === 1 ? '' : 's'} uploaded`);
    setUploading(false);
    refresh();
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this photo? This can\'t be undone.')) return;
    const r = await authedFetch(`/api/photos/${id}`, { method: 'DELETE' });
    if (r.ok) {
      setPhotos(prev => (prev || []).filter(p => p.id !== id));
      toast.success('Photo deleted');
    } else {
      toast.error('Failed to delete');
    }
  };

  const count = photos?.length ?? 0;
  const slotsLeft = MAX_PHOTOS - count;

  return (
    <div className="border-t border-slate-800 mt-3 pt-3">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 text-sm text-slate-300 hover:text-white"
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Camera size={16} className="text-blue-400" />
        <span className="font-medium">Photos</span>
        {photos != null && (
          <span className="text-xs text-slate-500 ml-1">{count} / {MAX_PHOTOS}</span>
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {!readonly && (
            <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed cursor-pointer text-sm transition
              ${uploading || slotsLeft <= 0
                ? 'border-slate-800 text-slate-600 cursor-not-allowed'
                : 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-slate-600'}`}>
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {slotsLeft <= 0
                ? 'Maximum reached — delete one to upload more'
                : uploading
                  ? 'Uploading…'
                  : `Upload photos (JPEG, PNG, WEBP, HEIC · max ${MAX_BYTES / 1024 / 1024}MB each)`}
              <input
                type="file"
                accept={ACCEPT}
                multiple
                disabled={uploading || slotsLeft <= 0}
                onChange={onUpload}
                className="hidden"
              />
            </label>
          )}

          {error && (
            <div className="flex items-center gap-2 text-rose-400 text-xs">
              <AlertTriangle size={14} /> Failed to load photos: {error}
            </div>
          )}

          {photos == null && !error && (
            <div className="flex justify-center py-4"><Loader2 size={20} className="text-slate-600 animate-spin" /></div>
          )}

          {photos && photos.length === 0 && (
            <p className="text-slate-500 text-xs italic">
              {readonly ? 'No photos uploaded.' : 'No photos yet — drop the first one above.'}
            </p>
          )}

          {photos && photos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {photos.map(p => (
                <Thumb
                  key={p.id}
                  photo={p}
                  onClick={() => setLightbox(p)}
                  onDelete={onDelete}
                  readonly={readonly}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <Lightbox photo={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
